/**
 * Transition Engine — centralizes View Transitions API usage.
 *
 * Responsibilities:
 *   - Start view transition
 *   - Set direction CSS custom properties for animation
 *   - Fallback for unsupported browsers
 *   - Return promise for lifecycle coordination
 */

import { flushSync } from 'svelte'
import { duration, easing } from '$lib/motion/tokens.js'

/** The transition currently animating, if any. @type {ViewTransition | null} */
let inFlight = null

/**
 * Run a page transition with directional awareness.
 *
 * Uses navigation graph semantics:
 *   push    — forward slide (new enters from right, old exits left)
 *   pop     — backward slide (new enters from left, old exits right)
 *   swap    — crossfade (no movement)
 *   overlay — scale up from center (modal/sheet)
 *
 * Sets CSS custom properties on <html> so ::view-transition-* pseudo-elements
 * can animate in the correct direction.
 *
 * @param {() => void} callback - DOM update to transition
 * @param {'push' | 'pop' | 'swap' | 'overlay'} [direction='push']
 * @returns {Promise<void>}
 */
export async function runTransition(callback, direction = 'push') {
  // Set direction CSS custom properties for view transition animations
  const { enterFrom, exitTo } = getDirectionOffsets(direction)
  document.documentElement.style.setProperty('--vt-enter-from', enterFrom)
  document.documentElement.style.setProperty('--vt-exit-to', exitTo)
  document.documentElement.style.setProperty('--vt-duration', `${duration.slow}ms`)
  document.documentElement.style.setProperty('--vt-easing', easing.navigation)
  document.documentElement.style.setProperty('--vt-type', direction)

  // The transition is decoration; the DOM update it wraps is the navigation.
  // So the commit is idempotent and is re-run after the transition settles —
  // an aborted transition never invokes its update callback, and without this
  // the URL moves while the page stays on the previous screen.
  let committed = false
  const commit = () => {
    if (committed) return
    committed = true
    // flushSync forces Svelte to apply the DOM update inside the callback.
    // Without it the update lands in a microtask and the browser may snapshot
    // the *new* state before the new page has actually rendered.
    flushSync(callback)
  }

  // Starting a transition while one is running skips the first, which tears
  // down its pseudo-elements mid-animation — every extracted element (the
  // header, the bottom bar) snaps back into place at once. Run the second
  // navigation without a transition instead.
  //
  // A document that is not being rendered is the other bar: Chrome rejects the
  // transition with InvalidStateError and drops the update callback on the
  // floor. That is routine inside a Telegram Mini App, whose webview reports
  // `hidden` while the sheet animates open and whenever Telegram is
  // backgrounded — the surface where every navigation appeared to do nothing.
  const canTransition =
    typeof document.startViewTransition === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    !inFlight &&
    document.visibilityState === 'visible'

  if (canTransition) {
    const transition = document.startViewTransition(commit)
    inFlight = transition
    // `ready` and `updateCallbackDone` reject on abort as well; nothing awaits
    // them, so without these they surface as unhandled rejections.
    transition.ready?.catch(() => {})
    transition.updateCallbackDone?.catch(() => {})
    try {
      await transition.finished
    } catch {
      // A skipped transition still commits the DOM update; an aborted one does
      // not, which is what the commit below covers.
    } finally {
      if (inFlight === transition) inFlight = null
    }
    commit()
  } else {
    commit()
  }

  // Clean up
  document.documentElement.style.removeProperty('--vt-enter-from')
  document.documentElement.style.removeProperty('--vt-exit-to')
  document.documentElement.style.removeProperty('--vt-duration')
  document.documentElement.style.removeProperty('--vt-easing')
  document.documentElement.style.removeProperty('--vt-type')
}

/**
 * Check if View Transitions API is supported.
 * @returns {boolean}
 */
export function supportsViewTransitions() {
  return 'startViewTransition' in document
}

/**
 * Get enter/exit offsets based on directional semantic.
 * @param {'push' | 'pop' | 'swap' | 'overlay'} semantic
 * @returns {{ enterFrom: string, exitTo: string }}
 */
function getDirectionOffsets(semantic) {
  switch (semantic) {
    case 'pop':
      return { enterFrom: '-20%', exitTo: '20%' }
    case 'swap':
      return { enterFrom: '0', exitTo: '0' } // crossfade
    case 'overlay':
      return { enterFrom: '0', exitTo: '0' } // pure crossfade (scale-up support needs CSS keyframe changes)
    case 'push':
    default:
      return { enterFrom: '20%', exitTo: '-20%' }
  }
}
